import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useBuilders, useUpdateBuilder } from '../api/builders';
import { useEntities, useCreateEntity } from '../api/entities';
import { useCommunities, useCopyCommunity, useCreateCommunity } from '../api/communities';
import { useSupplierSlots } from '../api/communitySuppliers';
import { downloadAuthorized } from '../api/client';
import {
  useCreateCustomerModel,
  useCustomerModels,
  useImportMultiMtoCustomer,
  useUploadCatalogModelMto,
} from '../api/customers';
import { useProductTypes, useFloorTypes, useScopes } from '../api/masterTables';
import { Breadcrumb } from '../components/ui/Breadcrumb';
import { Button } from '../components/ui/Button';
import { DropZone } from '../components/ui/DropZone';
import { DataTable } from '../components/ui/DataTable';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import {
  formatCommunityScopesLabel,
  ScopeCheckboxGroup,
} from '../components/community/ScopeCheckboxGroup';
import { useToast } from '../hooks/useToast';
import { sortByDisplayName } from '../lib/sortByName';
import { toastMtoPreserveResult } from '../lib/mtoPreserveSummary';
import page from './Page.module.css';
import styles from './BuilderDetailPage.module.css';

type Section = 'models' | 'communities';

const CREATE_NEW_ENTITY = '__new__';

type CatalogModelRow = {
  id: number;
  name: string;
  model_code?: string | null;
  elevation?: string | null;
  option_name?: string | null;
  product_type?: string | null;
  floor_type?: string | null;
  series?: string | null;
  mto_filename?: string | null;
  mto_line_count?: number;
  mto_unmatched_line_count?: number;
};

type BuilderCommunityRow = {
  id: number;
  name: string;
  num_lots?: number | null;
  city?: string | null;
  zip?: string | null;
  scopes?: Array<{ id: number; name: string; sort_order?: number }> | null;
  acceptedBid?: { revision?: number; revisionId?: number; acceptedAt?: string } | null;
  bidLocked?: boolean;
};

type NewCommunityFormState = {
  name: string;
  numLots: string;
  city: string;
  zip: string;
  scopeIds: number[];
  suppliers: Record<string, string>;
};

function emptyNewCommunityForm(): NewCommunityFormState {
  return {
    name: '',
    numLots: '',
    city: '',
    zip: '',
    scopeIds: [],
    suppliers: {},
  };
}

function IconEdit() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconExport() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function BuilderDetailPage() {
  const { builderId } = useParams<{ builderId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: builders } = useBuilders();
  const builder = builders?.find((b) => String(b.id) === builderId);
  const {
    data: communities,
    isLoading: communitiesLoading,
    refetch: refetchCommunities,
  } = useCommunities(builderId);
  const { data: catalogModels, isLoading: modelsLoading } = useCustomerModels(builderId);
  const { data: productTypes } = useProductTypes();
  const { data: floorTypes } = useFloorTypes();
  const { data: scopeRows } = useScopes();
  const createCommunity = useCreateCommunity(builderId);
  const copyCommunity = useCopyCommunity(builderId);
  const updateBuilder = useUpdateBuilder();
  const createEntity = useCreateEntity();
  const { data: entities } = useEntities();
  const createModel = useCreateCustomerModel(builderId);
  const uploadMto = useUploadCatalogModelMto(builderId);
  const importMultiMto = useImportMultiMtoCustomer(builderId);
  const { data: supplierSlotsData } = useSupplierSlots();

  const [section, setSection] = useState<Section>('models');

  useEffect(() => {
    if (section === 'communities' && builderId) {
      void refetchCommunities();
    }
  }, [section, builderId, refetchCommunities]);
  const [newCommunityForm, setNewCommunityForm] = useState<NewCommunityFormState>(() => emptyNewCommunityForm());

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSage, setEditSage] = useState('');
  const [editEntityId, setEditEntityId] = useState('');
  const [newEntityName, setNewEntityName] = useState('');

  const [newModelOpen, setNewModelOpen] = useState(false);
  const [newCommunityOpen, setNewCommunityOpen] = useState(false);
  const [copyCommunitySource, setCopyCommunitySource] = useState<BuilderCommunityRow | null>(null);
  const [copyCommunityName, setCopyCommunityName] = useState('');
  const [nmName, setNmName] = useState('');
  const [nmCode, setNmCode] = useState('');
  const [nmElev, setNmElev] = useState('');
  const [nmOption, setNmOption] = useState('');
  const [nmNotes, setNmNotes] = useState('');
  const [nmProductTypeId, setNmProductTypeId] = useState('');
  const [nmFloorTypeId, setNmFloorTypeId] = useState('');
  const [nmSeries, setNmSeries] = useState('');
  const [nmFile, setNmFile] = useState<File | null>(null);

  const mtoFileRef = useRef<HTMLInputElement>(null);
  const pendingUploadModelId = useRef<number | null>(null);

  function openEdit() {
    if (!builder) return;
    setEditName(String(builder.name ?? ''));
    setEditNotes(String((builder as { notes?: string | null }).notes ?? ''));
    setEditSage(String((builder as { sage_customer_id?: string | null }).sage_customer_id ?? ''));
    const eid = (builder as { entity_id?: number | null }).entity_id;
    setEditEntityId(eid != null ? String(eid) : '');
    setNewEntityName('');
    setEditOpen(true);
  }

  function resetNewCommunityForm() {
    setNewCommunityForm(emptyNewCommunityForm());
  }

  function resetNewModel() {
    setNmName('');
    setNmCode('');
    setNmElev('');
    setNmOption('');
    setNmNotes('');
    setNmProductTypeId('');
    setNmFloorTypeId('');
    setNmSeries('');
    setNmFile(null);
  }

  async function onSaveBuilder(e: React.FormEvent) {
    e.preventDefault();
    if (!builderId || !editName.trim()) return;
    try {
      let entityId: number | null = null;
      if (editEntityId === CREATE_NEW_ENTITY) {
        if (!newEntityName.trim()) {
          toast('Enter a name for the new entity', true);
          return;
        }
        const created = await createEntity.mutateAsync({ name: newEntityName.trim() });
        entityId = created.id;
      } else if (editEntityId) {
        entityId = Number(editEntityId);
      }
      await updateBuilder.mutateAsync({
        id: Number(builderId),
        name: editName.trim(),
        notes: editNotes.trim() || null,
        sage_customer_id: editSage.trim() || null,
        entity_id: entityId,
      });
      toast('Builder saved');
      setEditOpen(false);
    } catch (ex) {
      toast(ex instanceof Error ? ex.message : 'Save failed', true);
    }
  }

  async function onCreateModel(e: React.FormEvent) {
    e.preventDefault();
    if (!nmName.trim()) return;
    try {
      const res = await createModel.mutateAsync({
        name: nmName.trim(),
        model_code: nmCode.trim() || undefined,
        elevation: nmElev.trim() || undefined,
        option_name: nmOption.trim() || undefined,
        notes: nmNotes.trim() || undefined,
        product_type_id: nmProductTypeId ? Number(nmProductTypeId) : null,
        floor_type_id: nmFloorTypeId ? Number(nmFloorTypeId) : null,
        series: nmSeries.trim() || undefined,
        file: nmFile,
      });
      if (res.mtoError) {
        toast(`Model created; MTO: ${res.mtoError}`, true);
      } else {
        toast('Model created');
      }
      setNewModelOpen(false);
      resetNewModel();
    } catch (ex) {
      toast(ex instanceof Error ? ex.message : 'Create failed', true);
    }
  }

  function openCopyCommunity(row: BuilderCommunityRow) {
    setCopyCommunitySource(row);
    setCopyCommunityName(`Copy of ${row.name}`);
  }

  async function onCopyCommunity(e: React.FormEvent) {
    e.preventDefault();
    if (!copyCommunitySource || !copyCommunityName.trim()) return;
    try {
      const created = await copyCommunity.mutateAsync({
        communityId: copyCommunitySource.id,
        name: copyCommunityName.trim(),
      });
      toast('Community copied');
      setCopyCommunitySource(null);
      setCopyCommunityName('');
      navigate(`/communities/${created.id}`);
    } catch (ex) {
      toast(ex instanceof Error ? ex.message : 'Copy failed', true);
    }
  }

  async function onCreateCommunity(e: React.FormEvent) {
    e.preventDefault();
    if (!newCommunityForm.name.trim() || !builderId) return;
    const suppliersPayload: Record<string, string> = {};
    for (const [k, v] of Object.entries(newCommunityForm.suppliers)) {
      const t = String(v ?? '').trim();
      if (t) suppliersPayload[k] = t;
    }
    try {
      await createCommunity.mutateAsync({
        name: newCommunityForm.name.trim(),
        num_lots: newCommunityForm.numLots.trim() ? Number(newCommunityForm.numLots) : null,
        city: newCommunityForm.city.trim() || null,
        zip: newCommunityForm.zip.trim() || null,
        scope_ids: newCommunityForm.scopeIds,
        ...(Object.keys(suppliersPayload).length > 0 ? { suppliers: suppliersPayload } : {}),
      });
      resetNewCommunityForm();
      setNewCommunityOpen(false);
      toast('Community created');
    } catch (ex) {
      toast(ex instanceof Error ? ex.message : 'Create failed', true);
    }
  }

  function startMtoUpload(modelId: number) {
    pendingUploadModelId.current = modelId;
    mtoFileRef.current?.click();
  }

  function onMtoFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const mid = pendingUploadModelId.current;
    pendingUploadModelId.current = null;
    e.target.value = '';
    if (!file) return;
    if (mid == null) {
      toast('Could not determine which model to update. Try again.', true);
      return;
    }
    uploadMto.mutate(
      { modelId: mid, file },
      {
        onSuccess: (r) => toastMtoPreserveResult(toast, 'MTO uploaded', r.preserveSummary),
        onError: (err) => toast(err instanceof Error ? err.message : 'Upload failed', true),
      }
    );
  }

  const modelRows: CatalogModelRow[] = useMemo(
    () => sortByDisplayName((catalogModels ?? []) as CatalogModelRow[]),
    [catalogModels]
  );

  const modelColumns = useMemo<ColumnDef<CatalogModelRow, unknown>[]>(
    () => [
      {
        id: 'code',
        header: 'Code',
        accessorKey: 'model_code',
        meta: { nowrap: true, minWidth: '5.5rem' },
        cell: ({ row }) => row.original.model_code ?? '—',
      },
      {
        id: 'model',
        header: 'Model',
        accessorKey: 'name',
        meta: { minWidth: '10rem' },
        cell: ({ row }) => (
          <Link
            to={`/models/${row.original.id}`}
            style={{ fontWeight: 600, color: 'var(--color-navy)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'elevation',
        header: 'Elevation',
        accessorKey: 'elevation',
        meta: { minWidth: '5.5rem' },
        cell: ({ row }) => row.original.elevation ?? '—',
      },
      {
        id: 'option',
        header: 'Option',
        accessorKey: 'option_name',
        meta: { minWidth: '5.5rem' },
        cell: ({ row }) => row.original.option_name ?? '—',
      },
      {
        id: 'productType',
        header: 'Product Type',
        accessorKey: 'product_type',
        meta: { minWidth: '7.5rem' },
        cell: ({ row }) => row.original.product_type ?? '—',
      },
      {
        id: 'floorType',
        header: 'Floor Type',
        accessorKey: 'floor_type',
        meta: { minWidth: '6.5rem' },
        cell: ({ row }) => row.original.floor_type ?? '—',
      },
      {
        id: 'series',
        header: 'Series',
        accessorKey: 'series',
        meta: { minWidth: '5rem' },
        cell: ({ row }) => row.original.series ?? '—',
      },
      {
        id: 'mtoLines',
        header: 'MTO Lines',
        accessorKey: 'mto_line_count',
        meta: { align: 'right' as const, nowrap: true, minWidth: '7rem' },
        cell: ({ row }) => {
          const n = row.original.mto_line_count ?? 0;
          const unmatched = row.original.mto_unmatched_line_count ?? 0;
          if (!row.original.mto_filename && n === 0) return '—';
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
              <span>{n}</span>
              {unmatched > 0 ? (
                <span
                  className={styles.mtoUnmatchedWarning}
                  title={`${unmatched} line${unmatched === 1 ? '' : 's'} do not match the item master`}
                >
                  {unmatched} unmatched
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'mtoFile',
        header: 'MTO File',
        accessorKey: 'mto_filename',
        meta: { minWidth: '12rem', maxWidth: '20rem', wrap: true },
        cell: ({ row }) => {
          const fn = row.original.mto_filename;
          if (!fn) return <span className={page.muted}>—</span>;
          return <span title={fn}>{fn}</span>;
        },
      },
      {
        id: 'upload',
        header: '',
        enableSorting: false,
        meta: { align: 'right' as const, nowrap: true, width: '1%' },
        cell: ({ row }) => (
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={uploadMto.isPending}
            onClick={(e) => {
              e.stopPropagation();
              startMtoUpload(row.original.id);
            }}
          >
            Upload MTO
          </Button>
        ),
      },
    ],
    [uploadMto.isPending]
  );

  const communityRows: BuilderCommunityRow[] = useMemo(
    () => (communities ?? []) as BuilderCommunityRow[],
    [communities]
  );

  const communityColumns = useMemo<ColumnDef<BuilderCommunityRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }) => <span style={{ fontWeight: 600 }}>{row.original.name}</span>,
      },
      {
        id: 'lots',
        header: 'Number of lots',
        accessorKey: 'num_lots',
        meta: { align: 'right' as const },
        cell: ({ row }) => {
          const n = row.original.num_lots;
          if (n === null || n === undefined) return '—';
          return String(n);
        },
      },
      {
        id: 'city',
        header: 'City',
        accessorFn: (r) => {
          const v = r.city;
          return v && String(v).trim() ? String(v).trim() : '';
        },
        cell: ({ row }) => {
          const v = row.original.city;
          return v && String(v).trim() ? String(v).trim() : '—';
        },
      },
      {
        id: 'zip',
        header: 'Zip',
        accessorFn: (r) => {
          const v = r.zip;
          return v && String(v).trim() ? String(v).trim() : '';
        },
        cell: ({ row }) => {
          const v = row.original.zip;
          return v && String(v).trim() ? String(v).trim() : '—';
        },
      },
      {
        id: 'scope',
        header: 'Scope',
        accessorFn: (r) => formatCommunityScopesLabel(r.scopes),
        cell: ({ row }) => {
          const t = formatCommunityScopesLabel(row.original.scopes);
          if (!t) return '—';
          return (
            <span title={t} className={styles.scopeCell}>
              {t}
            </span>
          );
        },
      },
      {
        id: 'acceptedBid',
        header: 'Accepted Bid',
        accessorFn: (r) => {
          const rev = r.acceptedBid?.revision;
          if (rev == null && !r.bidLocked) return '';
          return rev != null ? `Rev ${rev}` : 'Accepted';
        },
        meta: { nowrap: true },
        cell: ({ row }) => {
          const rev = row.original.acceptedBid?.revision;
          if (rev == null && !row.original.bidLocked) return '—';
          const label = rev != null ? `Rev ${rev}` : 'Accepted';
          const when = row.original.acceptedBid?.acceptedAt;
          const title = when
            ? `Accepted ${new Date(when).toLocaleString()}`
            : 'This community has an accepted bid';
          return (
            <span title={title} style={{ fontWeight: 600, color: 'var(--color-brand-dark, #15803d)' }}>
              {label}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        meta: { nowrap: true },
        cell: ({ row }) => (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openCopyCommunity(row.original);
            }}
          >
            Copy
          </Button>
        ),
      },
    ],
    []
  );

  const newCommunityScopeOptions = useMemo(
    () =>
      (scopeRows ?? []).map((r) => ({
        id: Number(r.id),
        name: String(r.name ?? ''),
      })),
    [scopeRows]
  );

  const supplierSlots = supplierSlotsData?.slots ?? [];

  const sage = builder ? String((builder as { sage_customer_id?: string | null }).sage_customer_id ?? '').trim() : '';
  const notes = builder ? String((builder as { notes?: string | null }).notes ?? '').trim() : '';
  const entityName = builder ? String((builder as { entity_name?: string | null }).entity_name ?? '').trim() : '';

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Builders', to: '/builders' },
          { label: builder?.name ?? `Builder ${builderId}` },
        ]}
      />

      <input
        ref={mtoFileRef}
        type="file"
        accept=".xlsx,.xls"
        className={styles.hiddenFileInput}
        tabIndex={-1}
        onChange={onMtoFilePicked}
      />

      <div className={page.card}>
        <div className={styles.summaryTop}>
          <div>
            <h2 className={page.title} style={{ marginBottom: 0 }}>
              {builder?.name ?? 'Builder'}
            </h2>
            <dl className={styles.summaryMeta}>
              <dt>Entity</dt>
              <dd>{entityName || '—'}</dd>
              <dt>Sage Customer ID</dt>
              <dd>{sage || '—'}</dd>
              <dt>Notes</dt>
              <dd className={styles.notesDd}>{notes || '—'}</dd>
            </dl>
          </div>
          <div className={styles.summaryActions}>
            <button
              type="button"
              className={styles.iconBtn}
              title="Edit builder"
              aria-label="Edit Builder"
              onClick={openEdit}
            >
              <IconEdit />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              title="Export builder (Excel)"
              aria-label="Export Builder (Excel)"
              onClick={() =>
                void downloadAuthorized(`/api/builders/${builderId}/export`).catch((e) =>
                  toast(e instanceof Error ? e.message : 'Export failed', true)
                )
              }
            >
              <IconExport />
            </button>
          </div>
        </div>
      </div>

      <div className={page.tabs} style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={section === 'models' ? `${page.tab} ${page.tabActive}` : page.tab}
          onClick={() => setSection('models')}
        >
          Models
        </button>
        <button
          type="button"
          className={section === 'communities' ? `${page.tab} ${page.tabActive}` : page.tab}
          onClick={() => setSection('communities')}
        >
          Communities
        </button>
      </div>

      {section === 'models' ? (
        <div className={page.card}>
          <div className={styles.modelsToolbar}>
            <p className={styles.sectionIntro}>
              Catalog models for this builder.               Use the{' '}
              <button
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
                onClick={() =>
                  void downloadAuthorized('/api/template/multi-mto-xlsx').catch((e) =>
                    toast(e instanceof Error ? e.message : 'Download failed', true)
                  )
                }
              >
                multi-MTO template (XLSX)
              </button>{' '}
              to import one or many models from a single workbook; for a single model, include one sheet or one model
              block.
            </p>
            <Button type="button" variant="success" onClick={() => setNewModelOpen(true)}>
              New Model
            </Button>
          </div>
          <div style={{ marginTop: 12, marginBottom: 8 }}>
          <DropZone
            accept=".xlsx,.xls"
            onFiles={(files) => {
              if (importMultiMto.isPending) return;
              importMultiMto.mutate(files[0]!, {
                onSuccess: () => toast('Multi-MTO import finished'),
                onError: (e) => toast(e instanceof Error ? e.message : 'Import failed', true),
              });
            }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Multi-MTO import</h3>
            <p style={{ margin: 0 }}>
              {importMultiMto.isPending ? 'Importing…' : 'Drop workbook here or click to browse'}
            </p>
          </DropZone>
          </div>
          {modelsLoading ? <p className={page.muted}>Loading models…</p> : null}
          {!modelsLoading && modelRows.length === 0 ? (
            <p className={page.muted}>
              No models yet. Create one with &quot;New Model&quot; or use multi-MTO import above.
            </p>
          ) : null}
          {!modelsLoading && modelRows.length > 0 ? (
            <DataTable
              layout="content"
              data={modelRows}
              columns={modelColumns}
              searchKeys={[
                (r) => r.model_code,
                'name',
                (r) => r.elevation,
                (r) => r.option_name,
                (r) => r.product_type,
                (r) => r.floor_type,
                (r) => r.series,
                (r) => r.mto_filename,
                (r) => String(r.mto_line_count ?? ''),
              ]}
              onRowClick={(row) => navigate(`/models/${row.id}`)}
            />
          ) : null}
        </div>
      ) : null}

      {section === 'communities' ? (
        <div className={page.card}>
          <div className={styles.modelsToolbar}>
            <p className={styles.sectionIntro}>Communities for this builder.</p>
            <Button
              type="button"
              variant="success"
              onClick={() => {
                resetNewCommunityForm();
                setNewCommunityOpen(true);
              }}
            >
              New Community
            </Button>
          </div>
          {communitiesLoading ? <p className={page.muted}>Loading…</p> : null}
          {!communitiesLoading && communityRows.length === 0 ? (
            <p className={page.muted}>No communities yet. Create one with &quot;New Community&quot;.</p>
          ) : null}
          {!communitiesLoading && communityRows.length > 0 ? (
            <DataTable
              data={communityRows}
              columns={communityColumns}
              searchKeys={[
                'name',
                (r) => (r.num_lots == null ? '' : String(r.num_lots)),
                (r) => r.city,
                (r) => r.zip,
                (r) => formatCommunityScopesLabel(r.scopes),
                (r) => {
                  const rev = r.acceptedBid?.revision;
                  if (rev == null && !r.bidLocked) return '';
                  return rev != null ? `Rev ${rev}` : 'Accepted';
                },
              ]}
              onRowClick={(row) => navigate(`/communities/${row.id}`)}
            />
          ) : null}
        </div>
      ) : null}

      <Modal
        title="Edit Builder"
        open={editOpen}
        onClose={() => setEditOpen(false)}
        size="wide"
        showClose={false}
      >
        <form onSubmit={onSaveBuilder} className={styles.modalForm}>
          <Input label="Name" value={editName} onChange={(e) => setEditName(e.target.value)} required />
          <Select
            label="Entity"
            value={editEntityId}
            onChange={(e) => {
              setEditEntityId(e.target.value);
              if (e.target.value !== CREATE_NEW_ENTITY) setNewEntityName('');
            }}
          >
            <option value="">None</option>
            {(entities ?? []).map((ent) => (
              <option key={String(ent.id)} value={String(ent.id)}>
                {String(ent.name)}
              </option>
            ))}
            <option value={CREATE_NEW_ENTITY}>+ Add New Entity…</option>
          </Select>
          {editEntityId === CREATE_NEW_ENTITY ? (
            <Input
              label="New Entity Name"
              value={newEntityName}
              onChange={(e) => setNewEntityName(e.target.value)}
              required
              autoFocus
            />
          ) : null}
          <Input
            label="Sage Customer ID"
            value={editSage}
            onChange={(e) => setEditSage(e.target.value)}
          />
          <label className={page.muted} style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Notes
          </label>
          <textarea
            className={styles.textarea}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            rows={4}
          />
          <div className={styles.modalActions}>
            <Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="success" disabled={updateBuilder.isPending || createEntity.isPending}>
              Save
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        title="New Community"
        open={newCommunityOpen}
        onClose={() => {
          setNewCommunityOpen(false);
          resetNewCommunityForm();
        }}
        size="wide"
        showClose={false}
      >
        <form onSubmit={onCreateCommunity} className={styles.modalForm}>
          <Input
            label="Name"
            value={newCommunityForm.name}
            onChange={(e) => setNewCommunityForm((f) => ({ ...f, name: e.target.value }))}
            required
            autoFocus
          />
          <div className={page.row} style={{ flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: '1 1 140px' }}>
              <Input
                label="Lots"
                type="number"
                min={0}
                value={newCommunityForm.numLots}
                onChange={(e) => setNewCommunityForm((f) => ({ ...f, numLots: e.target.value }))}
              />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <Input
                label="City"
                value={newCommunityForm.city}
                onChange={(e) => setNewCommunityForm((f) => ({ ...f, city: e.target.value }))}
              />
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <Input
                label="ZIP"
                value={newCommunityForm.zip}
                onChange={(e) => setNewCommunityForm((f) => ({ ...f, zip: e.target.value }))}
              />
            </div>
          </div>
          <ScopeCheckboxGroup
            options={newCommunityScopeOptions}
            selectedIds={newCommunityForm.scopeIds}
            onChange={(scopeIds) => setNewCommunityForm((f) => ({ ...f, scopeIds }))}
          />
          <div>
            <h3 className={page.title} style={{ fontSize: 15, margin: '8px 0 6px' }}>
              Supplier by Sub-Phase
            </h3>
            <p className={page.muted} style={{ fontSize: 13, margin: '0 0 12px' }}>
              Optional. Pick one supplier per PTG-mapped sub-phase from the Admin allowlist.
            </p>
            {!supplierSlotsData ? (
              <p className={page.muted}>Loading supplier slots…</p>
            ) : !supplierSlots.length ? (
              <p className={page.muted}>No PTG-mapped sub-phases yet.</p>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 12,
                  alignItems: 'start',
                }}
              >
                {supplierSlots.map((slot) => {
                  const key = String(slot.sub_phase_id);
                  const emptyAllowlist = Boolean(slot.error);
                  return (
                    <div key={key}>
                      <Select
                        label={`${slot.phase_name} / ${slot.sub_phase_name}`}
                        value={newCommunityForm.suppliers[key] ?? ''}
                        disabled={emptyAllowlist}
                        onChange={(e) =>
                          setNewCommunityForm((f) => ({
                            ...f,
                            suppliers: { ...f.suppliers, [key]: e.target.value },
                          }))
                        }
                      >
                        <option value="">— None —</option>
                        {slot.eligible_suppliers.map((s) => (
                          <option key={s.id} value={s.name}>
                            {s.name}
                          </option>
                        ))}
                      </Select>
                      {emptyAllowlist ? (
                        <p style={{ color: 'var(--color-danger, #b91c1c)', fontSize: 12, margin: '4px 0 0' }}>
                          {slot.error}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className={styles.modalActions}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setNewCommunityOpen(false);
                resetNewCommunityForm();
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              disabled={createCommunity.isPending || !newCommunityForm.name.trim()}
            >
              {createCommunity.isPending ? 'Creating…' : 'Create Community'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        title="Copy Community"
        open={!!copyCommunitySource}
        onClose={() => {
          setCopyCommunitySource(null);
          setCopyCommunityName('');
        }}
        size="md"
        showClose={false}
      >
        <form onSubmit={onCopyCommunity} className={styles.modalForm}>
          <p className={page.muted} style={{ margin: '0 0 8px', fontSize: 13 }}>
            Source:{' '}
            <span style={{ fontWeight: 600, color: 'var(--color-navy, #0f172a)' }}>
              {copyCommunitySource?.name}
            </span>
          </p>
          <Input
            label="New Name"
            value={copyCommunityName}
            onChange={(e) => setCopyCommunityName(e.target.value)}
            required
            autoFocus
          />
          <p className={page.muted} style={{ margin: 0, fontSize: 13 }}>
            Copies settings, suppliers, on-site models, MTOs, labor, and related data. Bid revisions
            are not copied.
          </p>
          <div className={styles.modalActions}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCopyCommunitySource(null);
                setCopyCommunityName('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="success"
              disabled={copyCommunity.isPending || !copyCommunityName.trim()}
            >
              {copyCommunity.isPending ? 'Copying…' : 'Copy Community'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        title="New Model"
        open={newModelOpen}
        onClose={() => {
          setNewModelOpen(false);
          resetNewModel();
        }}
        size="wide"
        showClose={false}
      >
        <form onSubmit={onCreateModel} className={styles.modalForm}>
          <Input label="Model Name" value={nmName} onChange={(e) => setNmName(e.target.value)} required autoFocus />
          <div className={page.row} style={{ flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: '1 1 200px' }}>
              <Input label="Model Code" value={nmCode} onChange={(e) => setNmCode(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <Input label="Elevation" value={nmElev} onChange={(e) => setNmElev(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <Input label="Option" value={nmOption} onChange={(e) => setNmOption(e.target.value)} />
            </div>
          </div>
          <div className={page.row} style={{ flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: '1 1 200px' }}>
              <Select
                label="Product Type"
                value={nmProductTypeId}
                onChange={(e) => setNmProductTypeId(e.target.value)}
              >
                <option value="">None</option>
                {(productTypes ?? []).map((pt) => (
                  <option key={String(pt.id)} value={String(pt.id)}>
                    {String(pt.name)}
                  </option>
                ))}
              </Select>
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <Select
                label="Floor Type"
                value={nmFloorTypeId}
                onChange={(e) => setNmFloorTypeId(e.target.value)}
              >
                <option value="">None</option>
                {(floorTypes ?? []).map((ft) => (
                  <option key={String(ft.id)} value={String(ft.id)}>
                    {String(ft.name)}
                  </option>
                ))}
              </Select>
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <Input label="Series" value={nmSeries} onChange={(e) => setNmSeries(e.target.value)} placeholder="Optional" />
            </div>
          </div>
          <label className={page.muted} style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Notes
          </label>
          <textarea
            className={styles.textarea}
            value={nmNotes}
            onChange={(e) => setNmNotes(e.target.value)}
            placeholder="Optional"
            rows={3}
          />
          <label className={page.muted} style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Attach Multi-MTO Workbook (Optional)
          </label>
          <p className={page.muted} style={{ fontSize: 12, margin: '0 0 8px' }}>
            Use the{' '}
            <button
              type="button"
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'inherit',
                textDecoration: 'underline',
                cursor: 'pointer',
                font: 'inherit',
              }}
              onClick={() =>
                void downloadAuthorized('/api/template/multi-mto-xlsx').catch((e) =>
                  toast(e instanceof Error ? e.message : 'Download failed', true)
                )
              }
            >
              multi-MTO template
            </button>
            ; include one sheet or one model block that matches this model&apos;s name, elevation, and option.
          </p>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setNmFile(e.target.files?.[0] ?? null)}
          />
          {nmFile ? (
            <span className={page.muted} style={{ fontSize: 13 }}>
              Selected: {nmFile.name}
            </span>
          ) : null}
          <div className={styles.modalActions}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setNewModelOpen(false);
                resetNewModel();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="success" disabled={createModel.isPending || !nmName.trim()}>
              {createModel.isPending ? 'Creating…' : 'Create Model'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
